import * as React from "react"
import { omit } from "ramda"
import styled from "@oui/system"

import Box from "../Box"
import Inset from "../Inset"
import ToastWrapper from "./ToastWrapper"
import { getMeasurements } from "../useMeasure"
import useResize from "../useResize"
import useTheme from "../useTheme"

type PaddingProp = { padding: number }
type ToastContainerProp = {
  toastMap: Map<string, JSX.Element>
}

const ToastStackWrapper_ = (props: React.PropsWithChildren<PaddingProp>) => (
  <Box {...omit(["padding"], props)} />
)
const ToastStackWrapper = styled(ToastStackWrapper_)(({ padding }) => ({
  flexDirection: "row-reverse",
  height: "100%",
  left: 0,
  padding: `${padding}px`,
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  width: "100%"
}))

const ToastStackInnerWrapper = styled(Box)({ position: "relative" })

const ToastContainer = ({
  toastMap,
  padding
}: ToastContainerProp & PaddingProp) => {
  const [wrappedToasts, setWrappedToasts] = React.useState<JSX.Element[]>([])
  const resizeRef = React.useRef<HTMLElement>(document.body)

  // If we go with single toast at a time
  // const notification = toastMap.values().next().value;
  const toastStack = () =>
    Array.from(toastMap.entries()).map(([id, toast]: [string, JSX.Element]) => (
      <ToastWrapper id={id} key={id + "_wrapper"}>
        {toast}
      </ToastWrapper>
    ))

  const updatePosition = React.useCallback(() => {
    setWrappedToasts(prev => {
      const { toasts } = prev.reduce(
        ({ y, toasts }, wrappedToast) => {
          const {
            props: { id }
          } = wrappedToast
          const updatedToast = React.cloneElement(wrappedToast, { y })
          const element = document.getElementById(id)
          if (element !== null) {
            const { client } = getMeasurements(element, ["client"])
            client?.height && (y += client.height + padding)
          }
          toasts.push(updatedToast)
          return { y, toasts }
        },
        { y: 0, toasts: [] as JSX.Element[] }
      )
      return toasts
    })
    // eslint-disable-next-line
  }, [wrappedToasts])

  React.useEffect(() => {
    async function update() {
      await setWrappedToasts(toastStack())
      updatePosition()
    }
    toastMap.size && update()
    // eslint-disable-next-line
  }, [toastMap])

  // TODO: use theme values
  // https://jira.oci.oraclecorp.com/browse/OUI-409
  const debounceDelay = 250
  const { spacing } = useTheme()

  useResize(resizeRef.current, updatePosition, debounceDelay, false)

  return (
    <ToastStackWrapper padding={spacing.sm}>
      <Inset width={{ xs: "100%", md: "50%" }}>
        <ToastStackInnerWrapper>{wrappedToasts}</ToastStackInnerWrapper>
      </Inset>
    </ToastStackWrapper>
  )
}

export default ToastContainer
